<script lang="ts">
  import titleUrl from '../../../assets/source/title/title-4x.webp';

  interface Props {
    onreveal: () => void;
    onpanels: () => void;
    ondone: () => void;
  }

  const { onreveal, onpanels, ondone }: Props = $props();

  const FADE_MS = 300;
  const HOLD_MS = 350;
  const SHRINK_MS = 600;
  const SETTLE_MS = 200;
  const CROSSFADE_MS = 300;
  const SLIDE_MS = 500;

  const SHRINK_AT = FADE_MS + HOLD_MS;
  const REVEAL_AT = SHRINK_AT + SHRINK_MS + SETTLE_MS;
  const PANELS_AT = REVEAL_AT + CROSSFADE_MS;
  const DONE_AT = PANELS_AT + SLIDE_MS;

  const PEAK_WIDTH = 634;
  const SHRINK_STEPS = 24;
  const PLATE_WIDTH = 326;
  const PLATE_HEIGHT = 520;

  // Where Menu_1's baked logo sits inside the 326x520 plate, measured against title.png itself —
  // move these and the landing stops being seamless. docs/ui-title-upscale.md has the workings.
  const LOGO = { left: 35.4, top: 48.2, width: 235, height: 73.8 };

  let title: HTMLImageElement | undefined = $state();

  function landing(): { left: number; top: number } {
    const plate = document.querySelector('.wis-panel--main');
    const box = plate?.getBoundingClientRect();
    const left = box === undefined ? (window.innerWidth - PLATE_WIDTH) / 2 : box.left;
    const top = box === undefined ? (window.innerHeight - PLATE_HEIGHT) / 2 : box.top;
    return { left: left + LOGO.left, top: top + LOGO.top };
  }

  function place(element: HTMLImageElement): void {
    const { left, top } = landing();
    element.style.left = `${String(left)}px`;
    element.style.top = `${String(top)}px`;
    element.style.width = `${String(LOGO.width)}px`;
    element.style.height = `${String(LOGO.height)}px`;
  }

  function frame(remaining: number): string {
    const { left, top } = landing();
    const dx = (window.innerWidth / 2 - (left + LOGO.width / 2)) * remaining;
    const dy = (window.innerHeight / 2 - (top + LOGO.height / 2)) * remaining;
    const scale = (PEAK_WIDTH / LOGO.width) ** remaining;
    return `translate(${String(dx)}px, ${String(dy)}px) scale(${String(scale)})`;
  }

  // Scale reads logarithmically, so a ramp that is linear in `scale` already looks like it is
  // decelerating and an ease cancels against it. These steps are even in apparent size instead.
  function shrink(): Keyframe[] {
    return Array.from({ length: SHRINK_STEPS + 1 }, (_, step) => ({
      offset: step / SHRINK_STEPS,
      transform: frame(1 - step / SHRINK_STEPS),
    }));
  }

  $effect(() => {
    const element = title;
    if (element === undefined) return;

    place(element);
    element.style.transform = frame(1);

    const timers: number[] = [];
    let finished = false;

    const at = (delay: number, step: () => void): void => {
      timers.push(window.setTimeout(step, delay));
    };

    const finish = (): void => {
      if (finished) return;
      finished = true;
      for (const timer of timers) clearTimeout(timer);
      for (const animation of element.getAnimations()) animation.cancel();
      element.style.opacity = '0';
      onreveal();
      onpanels();
      ondone();
    };

    element.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: FADE_MS,
      easing: 'ease-out',
      fill: 'both',
    });

    at(SHRINK_AT, () => {
      element.animate(shrink(), {
        duration: SHRINK_MS,
        easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
        fill: 'forwards',
      });
    });

    at(REVEAL_AT, () => {
      element.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: CROSSFADE_MS,
        easing: 'linear',
        fill: 'forwards',
      });
      onreveal();
    });

    at(PANELS_AT, onpanels);
    at(DONE_AT, finish);

    const reposition = (): void => {
      place(element);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('pointerdown', finish);
    window.addEventListener('keydown', finish);

    return () => {
      for (const timer of timers) clearTimeout(timer);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('pointerdown', finish);
      window.removeEventListener('keydown', finish);
    };
  });
</script>

<img bind:this={title} class="intro__title" src={titleUrl} alt="" data-testid="intro-title" />

<style>
  .intro__title {
    position: fixed;
    z-index: 4;
    opacity: 0;
    transform-origin: center;
    pointer-events: none;
    will-change: transform, opacity;
  }
</style>
